import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { User } from '../firebase/api';
import capsatLogoDarkModeUrl from '../icons/capsat-logo-dark-mode.png';
import grassUrl from '../images/backgrounds/grass.jpg';
import sittingCircleUrl from '../images/backgrounds/sitting-circle-wood-floor.png';
import cocaColaUrl from '../images/caps/coca-cola.png';
import heinekenUrl from '../images/caps/heineken.png';
import { useAuth } from './authContext';

export type CustomizationCategory = 'cap' | 'glass' | 'towel' | 'background';

// `current` is allowed to return values above `target`; the picker still
// renders a capped "target / target" once the option is unlocked.
export interface UnlockCriterion {
  current: (user: User | null) => number;
  target: number;
}

// Already clamped to `[0, target]` so the picker can render `x / y` directly.
export interface UnlockProgress {
  current: number;
  target: number;
}

// Branded cap render data: textured top + solid skirt. The image's perimeter
// pixels should match `skirtColor` so the seam between the textured top and
// the solid skirt reads as one continuous coloured surface.
export interface CapBrand {
  imageUrl: string;
  skirtColor: string;
}

export interface GlassTint {
  body: string;
  rim: string;
}

// `imageUrl` is painted on top of the cloth; `bodyColor` shows through the
// texture's transparent pixels and around its edges.
export interface TowelMotive {
  imageUrl: string;
  bodyColor: string;
}

// Liquid colour palette poured into the glass. Body/surface are split so the
// meniscus reads as a slightly glossier highlight on top of a translucent
// column. Emissive terms keep the hue saturated when the blue-tinted glass
// and contact shadows desaturate it.
export interface DrinkTheme {
  bodyColor: string;
  bodyEmissive: string;
  bodyEmissiveIntensity: number;
  bodyOpacity: number;
  surfaceColor: string;
  surfaceEmissive: string;
  surfaceEmissiveIntensity: number;
  surfaceOpacity: number;
}

export const STANDARD_GLASS_TINT: GlassTint = {
  body: '#cfeaff',
  rim: '#e6f3ff',
};

export const BEER_THEME: DrinkTheme = {
  bodyColor: '#E87A10',
  bodyEmissive: '#C85A00',
  bodyEmissiveIntensity: 0.2,
  bodyOpacity: 0.75,
  surfaceColor: '#D97820',
  surfaceEmissive: '#B05200',
  surfaceEmissiveIntensity: 0.15,
  surfaceOpacity: 0.65,
};

// Body luminance is kept well above near-black so the procedural normal map's
// bump shading reads as visible 3D fluff rather than getting crushed into
// noise. The PNG is alpha-cut around the logo so the body colour shows
// through the rest of the towel.
export const STANDARD_TOWEL_MOTIVE: TowelMotive = {
  imageUrl: capsatLogoDarkModeUrl,
  bodyColor: '#4a4a4a',
};

// Yellow-leaning green so the cider still reads as fruity rather than swampy
// through the blue-tinted glass.
export const CIDER_THEME: DrinkTheme = {
  bodyColor: '#8FBE2A',
  bodyEmissive: '#5E8A18',
  bodyEmissiveIntensity: 0.2,
  bodyOpacity: 0.75,
  surfaceColor: '#82B025',
  surfaceEmissive: '#4E7414',
  surfaceEmissiveIntensity: 0.15,
  surfaceOpacity: 0.65,
};

export interface CustomizationOption {
  id: string;
  label: string;
  description?: string;
  iconUrl?: string;
  // When omitted, the option is always unlocked — that's how the per-category
  // default option is modelled, so it stays selectable even before the user
  // document loads.
  criterion?: UnlockCriterion;
  // Replaces the option's label, description and progress with a generic
  // placeholder in the picker until its criterion is met, so the player can
  // see a hidden cosmetic exists without learning what it is. Used for
  // surprise / easter-egg cosmetics. The branded random-cap pool still skips
  // hidden+locked options through the regular `locked` filter.
  hidden?: boolean;
  // Each option only fills in the field that matches its parent category; the
  // others stay undefined. Keeping all four on the base option type (instead
  // of category-specific subtypes) lets the catalogue array stay flat.
  cap?: CapBrand;
  glassTint?: GlassTint;
  drinkTheme?: DrinkTheme;
  towel?: TowelMotive;
  backgroundUrl?: string;
}

export interface CustomizationCategoryDef {
  id: CustomizationCategory;
  label: string;
  // Selected by default and always unlocked.
  defaultOptionId: string;
  options: ReadonlyArray<CustomizationOption>;
}

// CustomizationOption with the per-user `locked` flag and `progress` snapshot
// resolved against the current user, so render code can branch on a plain
// boolean and pull "x / y" off the option directly.
export interface EffectiveCustomizationOption extends CustomizationOption {
  locked: boolean;
  progress: UnlockProgress | null;
}

export interface EffectiveCustomizationCategoryDef
  extends Omit<CustomizationCategoryDef, 'options'> {
  options: ReadonlyArray<EffectiveCustomizationOption>;
}

// Single source of truth for the customize menu. Achievement-gated options
// pair an `UnlockCriterion` with the description that explains what the
// player has to do, so the customize page can render both the prose and the
// machine-readable progress side by side and react in real time as the
// user's stats update via the Firestore snapshot listener.
export const CUSTOMIZATION_CATEGORIES: ReadonlyArray<CustomizationCategoryDef> =
  [
    {
      id: 'cap',
      label: 'Kapsyl',
      defaultOptionId: 'random',
      options: [
        {
          id: 'random',
          label: 'Slumpvis',
        },
        {
          id: 'cocacola',
          label: 'Cola-Coca',
          description: 'Träffa 25 kapsyler i rad',
          iconUrl: cocaColaUrl,
          criterion: {
            current: user => user?.gameHighscore ?? 0,
            target: 25,
          },
          cap: {
            imageUrl: cocaColaUrl,
            skirtColor: '#e00000',
          },
        },
        {
          id: 'heineken',
          label: 'Hajneken',
          description: 'Träffa 50 kapsyler i rad',
          iconUrl: heinekenUrl,
          criterion: {
            current: user => user?.gameHighscore ?? 0,
            target: 50,
          },
          cap: {
            imageUrl: heinekenUrl,
            // Heineken dark green. Noticeably darker than the in-game rainbow
            // green so we deliberately don't share that material.
            skirtColor: '#0a5e2a',
          },
        },
        {
          id: 'test',
          label: 'Test',
          description: 'Träffa 100 kapsyler i rad',
          iconUrl: heinekenUrl,
          criterion: {
            current: () => 0,
            target: 100,
          },
          hidden: true,
          cap: {
            imageUrl: heinekenUrl,
            skirtColor: '#0a5e2a',
          },
        },
      ],
    },
    {
      id: 'glass',
      label: 'Glas',
      defaultOptionId: 'standard',
      options: [
        {
          id: 'standard',
          label: 'Standard',
          glassTint: STANDARD_GLASS_TINT,
          drinkTheme: BEER_THEME,
        },
        {
          id: 'cider',
          label: 'Cider',
          description: 'Fyll 1 glas med kapsyler',
          criterion: {
            current: user => user?.gameFilledGlasses ?? 0,
            target: 1,
          },
          // Cider only swaps the liquid; the glass itself stays standard.
          glassTint: STANDARD_GLASS_TINT,
          drinkTheme: CIDER_THEME,
        },
      ],
    },
    {
      id: 'towel',
      label: 'Handduk',
      defaultOptionId: 'standard',
      options: [
        {
          id: 'standard',
          label: 'Standard',
          towel: STANDARD_TOWEL_MOTIVE,
        },
      ],
    },
    {
      id: 'background',
      label: 'Bakgrund',
      defaultOptionId: 'standard',
      options: [
        {
          id: 'standard',
          label: 'Standard',
          backgroundUrl: sittingCircleUrl,
        },
        {
          id: 'grass',
          label: 'Utomhus',
          description: 'Kasta 250 kapsyler',
          criterion: {
            current: user => user?.gameCapsThrown ?? 0,
            target: 250,
          },
          backgroundUrl: grassUrl,
        },
      ],
    },
  ];

export type CustomizationSelection = Record<CustomizationCategory, string>;

const DEFAULT_SELECTION: CustomizationSelection =
  CUSTOMIZATION_CATEGORIES.reduce((acc, category) => {
    acc[category.id] = category.defaultOptionId;
    return acc;
  }, {} as CustomizationSelection);

const STORAGE_KEY = 'gameCustomization';

export const getCategoryDef = (
  category: CustomizationCategory,
): CustomizationCategoryDef => {
  const def = CUSTOMIZATION_CATEGORIES.find(c => c.id === category);
  if (def === undefined) {
    throw new Error(`Unknown customization category: ${category}`);
  }
  return def;
};

export const getOptionDef = (
  category: CustomizationCategory,
  optionId: string,
): CustomizationOption | undefined =>
  getCategoryDef(category).options.find(o => o.id === optionId);

// The render-data lookups below fall back to sensible defaults so a stale
// stored selection — e.g. an option removed from the catalogue between
// releases — doesn't break rendering.

export const getGlassTint = (optionId: string): GlassTint =>
  getOptionDef('glass', optionId)?.glassTint ?? STANDARD_GLASS_TINT;

export const getDrinkTheme = (optionId: string): DrinkTheme =>
  getOptionDef('glass', optionId)?.drinkTheme ?? BEER_THEME;

export const getTowelMotive = (optionId: string): TowelMotive =>
  getOptionDef('towel', optionId)?.towel ?? STANDARD_TOWEL_MOTIVE;

export const getBackgroundUrl = (optionId: string): string =>
  getOptionDef('background', optionId)?.backgroundUrl ?? sittingCircleUrl;

export const isOptionUnlocked = (
  option: CustomizationOption,
  user: User | null,
): boolean =>
  option.criterion === undefined ||
  option.criterion.current(user) >= option.criterion.target;

export const getOptionProgress = (
  option: CustomizationOption,
  user: User | null,
): UnlockProgress | null => {
  if (option.criterion === undefined) return null;
  const raw = option.criterion.current(user);
  const target = option.criterion.target;
  return {
    current: Math.max(0, Math.min(raw, target)),
    target,
  };
};

// Drops keys that no longer exist in the catalogue and falls back to each
// category's default when the stored option is unknown or has since been
// re-locked.
const normaliseSelection = (
  raw: Partial<Record<string, unknown>>,
  user: User | null,
): CustomizationSelection => {
  const next: CustomizationSelection = { ...DEFAULT_SELECTION };
  for (const category of CUSTOMIZATION_CATEGORIES) {
    const stored = raw[category.id];
    if (typeof stored !== 'string') continue;
    const option = getOptionDef(category.id, stored);
    if (option === undefined) continue;
    if (!isOptionUnlocked(option, user)) continue;
    next[category.id] = stored;
  }
  return next;
};

const readStoredSelection = (user: User | null): CustomizationSelection => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return { ...DEFAULT_SELECTION };
    const parsed = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') {
      return { ...DEFAULT_SELECTION };
    }
    return normaliseSelection(parsed as Record<string, unknown>, user);
  } catch {
    return { ...DEFAULT_SELECTION };
  }
};

interface CustomizationContextType {
  categories: ReadonlyArray<EffectiveCustomizationCategoryDef>;
  selection: CustomizationSelection;
  // Returns whether the selection was applied. Fails if the option doesn't
  // exist or is locked for the current user.
  selectOption: (category: CustomizationCategory, optionId: string) => boolean;
}

const buildEffectiveCategories = (
  user: User | null,
): ReadonlyArray<EffectiveCustomizationCategoryDef> =>
  CUSTOMIZATION_CATEGORIES.map(category => ({
    ...category,
    options: category.options.map(option => ({
      ...option,
      locked: !isOptionUnlocked(option, user),
      progress: getOptionProgress(option, user),
    })),
  }));

const CustomizationContext = createContext<CustomizationContextType>({
  categories: buildEffectiveCategories(null),
  selection: { ...DEFAULT_SELECTION },
  selectOption: () => false,
});

export function useCustomization() {
  return useContext(CustomizationContext);
}

export default function CustomizationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { user } = useAuth();

  const [selection, setSelection] = useState<CustomizationSelection>(() =>
    readStoredSelection(user),
  );

  // Re-validate the selection whenever the user document changes so a
  // previously-unlocked option that got re-locked (e.g. a server-side stat
  // reset) falls back to the category default rather than rendering a
  // cosmetic the player no longer qualifies for.
  useEffect(() => {
    setSelection(prev => {
      const validated = normaliseSelection(prev, user);
      // Preserve reference identity when nothing changed so downstream
      // useMemos don't churn on every snapshot.
      const sameKeys = Object.keys(validated) as CustomizationCategory[];
      const unchanged = sameKeys.every(key => validated[key] === prev[key]);
      return unchanged ? prev : validated;
    });
  }, [user]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
    } catch {
      // localStorage may be disabled (e.g. private mode); the selection still
      // persists for the current session via state.
    }
  }, [selection]);

  const categories = useMemo(() => buildEffectiveCategories(user), [user]);

  const selectOption = useCallback(
    (category: CustomizationCategory, optionId: string) => {
      const option = getOptionDef(category, optionId);
      if (option === undefined) return false;
      if (!isOptionUnlocked(option, user)) return false;
      setSelection(prev =>
        prev[category] === optionId ? prev : { ...prev, [category]: optionId },
      );
      return true;
    },
    [user],
  );

  const value = useMemo(
    () => ({ categories, selection, selectOption }),
    [categories, selection, selectOption],
  );

  return (
    <CustomizationContext.Provider value={value}>
      {children}
    </CustomizationContext.Provider>
  );
}
